import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { BalanceTabComponent } from './balance-tab/balance-tab.component';
import { JourneyTabComponent } from './journey-tab/journey-tab.component';
import { TabsV2Component } from 'shared/components/tabs-v2/tabs-v2.component';
import { TranslatePipe } from '@ngx-translate/core';
import { UserAvatarV2Component } from 'shared/components/user-avatar-v2/user-avatar-v2.component';
import { NzModalService } from 'ng-zorro-antd/modal';
import { User, UserTypes } from 'shared/types/user.model';
import { ShareGanasModalComponent } from '../../../shared/modal/share-ganas-modal/share-ganas-modal.component';
import { AuthService } from 'core/services/auth.service';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { HideTooltipOnScrollDirective } from 'shared/directives/hide-tooltip-on-scroll.directive';
import { isDeletedAccountName } from 'shared/utils/deleted-account.utils';

const LevelNames = {
  1: 'my-forest.muirWoods',
  2: 'my-forest.greatSmokyMountins',
  3: 'my-forest.cherokee',
  4: 'my-forest.shawnee',
  5: 'my-forest.ocala',
  6: 'my-forest.tongass'
};

enum ETab {
  JOURNEY = 1,
  BALANCE = 2
}

export const ENCOURAGE_BUBBLE_VISIBLE_MS = 10000;

export function canShowEncourageBubble(isGanarbol: boolean, isGanacuate: boolean): boolean {
  return isGanarbol || isGanacuate;
}

export function shouldShowEncourageBubble(
  isInitialVisible: boolean,
  isForestImageHovered: boolean,
  canShowBubble: boolean
): boolean {
  return canShowBubble && (isInitialVisible || isForestImageHovered);
}

export function shouldShowDirectContactAction(isIndirectMember: boolean): boolean {
  return !isIndirectMember;
}

export function shouldShowCommunityMetrics(isIndirectMember: boolean): boolean {
  return !isIndirectMember;
}

export function shouldShowReferredBy(isIndirectMember: boolean, hasReferredBy: boolean): boolean {
  return isIndirectMember && hasReferredBy;
}

export function shouldShowMobileDirectReferralContent(isIndirectMember: boolean): boolean {
  return !isIndirectMember;
}

export function shouldShowMobileReferredBy(isIndirectMember: boolean, hasReferredBy: boolean): boolean {
  return isIndirectMember && hasReferredBy;
}

export function isReferredByCurrentUser(user: User | null, currentUser: User | null): boolean {
  if (!user || !currentUser || currentUser.id == null || user.id === currentUser.id) {
    return false;
  }

  const referredBy = user.userObj?.referred_by;

  if (typeof referredBy !== 'string') {
    if (typeof referredBy?.is_self === 'boolean') {
      return referredBy.is_self;
    }

    return referredBy?.id === currentUser.id;
  }

  return user.isDirectReferral && normalizeReferredByName(referredBy) === normalizeReferredByName(currentUser.fullName);
}

export function formatReferredByDisplayName(name?: string | null): string {
  const normalizedName = (name || '').trim().replace(/\s+/g, ' ');

  if (!normalizedName || normalizedName === '-') {
    return '-';
  }

  if (isDeletedAccountName(normalizedName)) {
    return normalizedName;
  }

  const nameParts = normalizedName.split(' ');

  if (nameParts.length === 1) {
    return normalizedName;
  }

  const firstName = nameParts[0];
  const lastName = nameParts[nameParts.length - 1].replace(/\./g, '');
  const lastInitial = lastName.charAt(0).toUpperCase();

  return lastInitial ? `${firstName} ${lastInitial}.` : firstName;
}

function normalizeReferredByName(name?: string): string {
  return (name || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

@Component({
  selector: 'app-my-forest-user-info-default',
  imports: [
    BalanceTabComponent,
    JourneyTabComponent,
    TabsV2Component,
    TranslatePipe,
    UserAvatarV2Component,
    NzTooltipDirective,
    HideTooltipOnScrollDirective
  ],
  templateUrl: './my-forest-user-info-default.component.html',
  styleUrl: './my-forest-user-info-default.component.scss',
  animations: [
    trigger('encouragePopoverAnimation', [
      state('hidden', style({ opacity: 0, transform: 'translateY(4rem) scale(0.96)' })),
      state('visible', style({ opacity: 1, transform: 'translateY(0) scale(1)' })),
      transition('hidden => visible', animate('520ms cubic-bezier(0.16, 1, 0.3, 1)')),
      transition('visible => hidden', animate('220ms ease-in'))
    ])
  ]
})
export class MyForestUserInfoDefaultComponent {
  #modalService = inject(NzModalService);
  #authService = inject(AuthService);

  user = input<User>(null);
  userTimeline = input<any>(null);
  ganasShared = input<number>(0);
  ganamigoGanasShared = input<number>(0);
  isIndirectMember = input<boolean>(false);

  public ETab = ETab;

  currentForestName = computed(() => {
    const currentLevel = this.user()?.level?.id;
    return LevelNames[currentLevel];
  });

  currentForestImage = computed(() => {
    const currentLevel = this.user()?.level?.id;
    return `assets/img/my-forest/dk-l${currentLevel}.webp`;
  });

  encourageText = computed(() => {
    const userType = this.user()?.userType;
    return userType === UserTypes.MU ? 'my-forest.encourageYourGanarbol' : 'my-forest.encourageYourGanacuate';
  });

  isGanacuate = computed(() => !!this.user()?.isGanacuate);
  isGanarbol = computed(() => !!this.user()?.isGanarbol);

  referredByName = computed(() => formatReferredByDisplayName(this.user()?.referredByFullName));
  hasReferredBy = computed(() => this.referredByName() !== '-');
  showCurrentUserReferrerSuffix = computed(() =>
    isReferredByCurrentUser(this.user(), this.#authService.currentUser$() || this.#authService.currentUser)
  );
  isInitialEncourageVisible = signal(false);
  isForestImageHovered = signal(false);
  canShowEncourageBubble = computed(
    () => !this.user()?.isDeleted && canShowEncourageBubble(this.isGanarbol(), this.isGanacuate())
  );
  showEncourageMessage = computed(() =>
    shouldShowEncourageBubble(
      this.isInitialEncourageVisible(),
      this.isForestImageHovered(),
      this.canShowEncourageBubble()
    )
  );
  showDirectContactAction = computed(
    () => !this.user()?.isDeleted && shouldShowDirectContactAction(this.isIndirectMember())
  );
  showCommunityMetrics = computed(() => shouldShowCommunityMetrics(this.isIndirectMember()));
  showReferredBy = computed(() => shouldShowReferredBy(this.isIndirectMember(), this.hasReferredBy()));
  showMobileDirectReferralContent = computed(() => shouldShowMobileDirectReferralContent(this.isIndirectMember()));
  showMobileReferredBy = computed(() => shouldShowMobileReferredBy(this.isIndirectMember(), this.hasReferredBy()));

  #encourageVisibilityEffect = effect(onCleanup => {
    const canShowBubble = this.canShowEncourageBubble();

    this.isInitialEncourageVisible.set(false);
    this.isForestImageHovered.set(false);

    if (!canShowBubble) {
      return;
    }

    let showFrameId: number | undefined;
    let hideTimerId: number | undefined;
    const startFrameId = window.requestAnimationFrame(() => {
      showFrameId = window.requestAnimationFrame(() => {
        this.isInitialEncourageVisible.set(true);
        hideTimerId = window.setTimeout(() => this.isInitialEncourageVisible.set(false), ENCOURAGE_BUBBLE_VISIBLE_MS);
      });
    });

    onCleanup(() => {
      window.cancelAnimationFrame(startFrameId);

      if (showFrameId) {
        window.cancelAnimationFrame(showFrameId);
      }

      if (hideTimerId) {
        window.clearTimeout(hideTimerId);
      }
    });
  });

  tabs = signal([
    { id: ETab.JOURNEY, label: 'my-forest.journey' },
    { id: ETab.BALANCE, label: 'my-forest.balance' }
  ]);
  selectedTab = signal<ETab>(ETab.JOURNEY);

  public shareGanas(): void {
    if (!this.user() || this.user().isDeleted) {
      return;
    }
    this.#modalService.create({
      nzContent: ShareGanasModalComponent,
      nzData: {
        recipient: this.user()
      },
      nzClosable: false,
      nzWrapClassName: 'modal-wrap',
      nzWidth: 555,
      nzClassName: 'modal-dialog'
    });
  }

  public sendWhatsAppMessage(): void {
    const phoneNumber = this.user()?.fullPhoneNumber;
    if (this.user()?.isDeleted || !phoneNumber) {
      return;
    }
    const whatsappDesktopUrl = `whatsapp://send?phone=${phoneNumber}`;

    window.open(whatsappDesktopUrl, '_blank');
  }

  public showEncourageOnHover(): void {
    this.isForestImageHovered.set(true);
  }

  public hideEncourageOnHoverEnd(): void {
    this.isForestImageHovered.set(false);
  }
}
